import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api/client';
import { Sparkline } from '../components/Sparkline';
import { ProgressRing } from '../components/ProgressRing';
import type { Conseiller, ConseillerParticipant, ConseillersResponse, ConseillerDetailResponse, RevenueMonth, PresenceMonth } from '../types';
import { useT, useFormatDate } from '../i18n';

type TFn = (key: string, params?: Record<string, string | number>) => string;

function fmtCHF(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.0', '')} M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1).replace('.0', '')}k`;
  return n.toLocaleString('fr-CH');
}

function fmtH(h: number): string {
  return h % 1 === 0 ? `${h} h` : `${h.toFixed(1).replace('.', ',')} h`;
}

function fmtDate(d: string | null, fd: ReturnType<typeof useFormatDate>): string {
  if (!d) return '—';
  return fd.short(d);
}

function relativeAge(d: string | null, t: TFn): string {
  if (!d) return '';
  const years = Math.floor((Date.now() - new Date(d).getTime()) / (365.25 * 86_400_000));
  if (years < 1) {
    const months = Math.floor((Date.now() - new Date(d).getTime()) / (30.44 * 86_400_000));
    return t('conseillers.relative.months', { n: months });
  }
  return t('conseillers.relative.years', { n: years });
}

function monthAbbrev(m: number, t: TFn): string {
  return t(`conseillers.month.${m}`);
}

type Level = 'ok' | 'warn' | 'err' | 'neutral';
function presenceLevel(r: number | null): Level { return r === null ? 'neutral' : r >= 80 ? 'ok' : r >= 60 ? 'warn' : 'err'; }
function daysLevel(d: number | null): Level { return d === null ? 'neutral' : d <= 15 ? 'err' : d <= 30 ? 'warn' : 'ok'; }
function levelColor(l: Level): string {
  return l === 'ok' ? 'var(--ok)' : l === 'warn' ? 'var(--warn)' : l === 'err' ? 'var(--err)' : 'var(--ink-3)';
}

type SortKey = 'name' | 'organization' | 'active' | 'ca' | 'lifetime' | 'total';

function InfoTip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="infotip" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      ?
      {show && <div className="infotip__popup">{text}</div>}
    </span>
  );
}

/* ═══ MonthlyBarChart — matches WeeklyChart style, fills container width ═══ */
function MonthlyBarChart({ data, barColor = 'var(--accent)', unit = '', formatValue, rollingWindow = 3 }: {
  data: Array<{ month: string; value: number; label?: string }>;
  barColor?: string;
  unit?: string;
  formatValue?: (v: number) => string;
  rollingWindow?: number;
}) {
  const { t } = useT();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const containerRef = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(800);

  if (data.length === 0) return null;

  const fmt = formatValue || ((v: number) => {
    if (unit === 'CHF') return v.toLocaleString('fr-CH');
    if (unit === '%') return `${Math.round(v)}`;
    if (v % 1 === 0) return String(v);
    return v.toFixed(1).replace('.', ',');
  });

  // Y-axis: round max up to nice number
  const rawMax = Math.max(...data.map(d => d.value), 1);
  const step = rawMax > 10000 ? 5000 : rawMax > 1000 ? 500 : rawMax > 100 ? 20 : rawMax > 10 ? 5 : 1;
  const maxVal = Math.ceil(rawMax / step) * step || 10;

  const tickCount = 5;
  const yTicks: number[] = [];
  for (let i = 0; i < tickCount; i++) yTicks.push(Math.round((maxVal / (tickCount - 1)) * i));

  // Rolling average
  const rollingAvg: (number | null)[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < rollingWindow - 1) { rollingAvg.push(null); continue; }
    let sum = 0;
    for (let j = i - rollingWindow + 1; j <= i; j++) sum += data[j].value;
    rollingAvg.push(sum / rollingWindow);
  }

  // Month labels
  const monthLabels: { index: number; label: string }[] = [];
  let prevYear = -1;
  for (let i = 0; i < data.length; i++) {
    const parts = data[i].month.split('-');
    const m = parseInt(parts[1]);
    const y = parseInt(parts[0]);
    const show = data.length <= 24 || m === 1 || m === 4 || m === 7 || m === 10;
    if (show) {
      monthLabels.push({ index: i, label: `${monthAbbrev(m, t)}${m === 1 || y !== prevYear ? ` '${String(y).slice(2)}` : ''}` });
      prevYear = y;
    }
  }

  // Layout — compute bar width to fill container
  const yAxisW = unit === 'CHF' ? 52 : 34;
  const chartH = 180;
  const topPad = 10;
  const labelH = 20;
  const svgH = topPad + chartH + labelH;

  // Bars fill available width: gap = 30% of bar+gap unit
  const availW = containerWidth - yAxisW;
  const cellW = availW / data.length;
  const gap = Math.max(1, Math.min(cellW * 0.25, 4));
  const barW = Math.max(4, cellW - gap);
  const totalW = containerWidth;

  // Measure container on mount/resize
  const measureRef = (el: HTMLDivElement | null) => {
    if (el) {
      const w = el.getBoundingClientRect().width;
      if (Math.abs(w - containerWidth) > 5) setContainerWidth(w);
      if (!containerRef[0]) {
        containerRef[0] = el;
        const ro = new ResizeObserver(entries => {
          for (const e of entries) setContainerWidth(e.contentRect.width);
        });
        ro.observe(el);
      }
    }
  };

  return (
    <div className="weekly-bars" style={{ position: 'relative' }} ref={measureRef}>
      <svg width="100%" height={svgH} viewBox={`0 0 ${totalW} ${svgH}`}
        preserveAspectRatio="none" style={{ display: 'block' }}>

        {/* Y-axis labels + gridlines */}
        {yTicks.map(tick => {
          const y = topPad + chartH - (tick / maxVal) * chartH;
          return (
            <g key={tick}>
              <text x={yAxisW - 4} y={y + 3} textAnchor="end" fontSize="9" fill="var(--ink-3)">
                {fmt(tick)}
              </text>
              <line x1={yAxisW} y1={y} x2={totalW} y2={y} stroke="var(--line)" strokeWidth="0.5" />
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const barH = maxVal > 0 ? (d.value / maxVal) * chartH : 0;
          const x = yAxisW + i * cellW;
          const opacity = i === data.length - 1 ? 1 : 0.4 + (i / data.length) * 0.5;
          const isHovered = hoveredIdx === i;
          return (
            <rect key={d.month} x={x} y={topPad + chartH - barH}
              width={barW} height={Math.max(0, barH)} rx={1}
              fill={isHovered ? 'var(--ink-1)' : barColor}
              opacity={isHovered ? 0.9 : opacity}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ cursor: 'default' }}
            />
          );
        })}

        {/* Rolling average polyline */}
        {(() => {
          const points: string[] = [];
          for (let i = 0; i < rollingAvg.length; i++) {
            const val = rollingAvg[i];
            if (val == null) continue;
            const x = yAxisW + i * cellW + barW / 2;
            const y = topPad + chartH - (val / maxVal) * chartH;
            points.push(`${x},${y}`);
          }
          if (points.length < 2) return null;
          return (
            <polyline points={points.join(' ')} fill="none" stroke="var(--ink-2)"
              strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity={0.5} />
          );
        })()}

        {/* X-axis month labels */}
        {monthLabels.map(({ index, label }) => {
          const x = yAxisW + index * cellW;
          return (
            <text key={`ml-${index}`} x={x} y={topPad + chartH + 14}
              textAnchor="start" fontSize="9" fill="var(--ink-3)">
              {label}
            </text>
          );
        })}
      </svg>

      {/* Tooltip — positioned next to bar, flips when near edge */}
      {hoveredIdx != null && (() => {
        const d = data[hoveredIdx];
        const barX = yAxisW + hoveredIdx * cellW + barW + 6;
        const flipLeft = barX + 160 > totalW;
        const tipLeft = flipLeft ? yAxisW + hoveredIdx * cellW - 6 : barX;
        const m = parseInt(d.month.split('-')[1]);
        const y = d.month.split('-')[0];
        return (
          <div className="weekly-bars__tooltip" style={{
            position: 'absolute', top: topPad + 4, left: tipLeft,
            transform: flipLeft ? 'translateX(-100%)' : undefined,
          }}>
            <div><strong>{monthAbbrev(m, t)} {y}</strong></div>
            <div className="weekly-bars__tooltip-row">
              <span className="weekly-bars__tooltip-label">{t('conseillers.chart.value')}</span>
              <strong>{fmt(d.value)}{unit ? ` ${unit}` : ''}</strong>
            </div>
            {rollingAvg[hoveredIdx] != null && (
              <div className="weekly-bars__tooltip-row">
                <span className="weekly-bars__tooltip-label">{t('conseillers.chart.rollingAvg', { n: rollingWindow })}</span>
                <strong>{fmt(rollingAvg[hoveredIdx]!)}{unit ? ` ${unit}` : ''}</strong>
              </div>
            )}
            {d.label && (
              <div className="weekly-bars__tooltip-row">
                <span className="weekly-bars__tooltip-label">{d.label}</span>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ═══ MetricCell — compact metric for card headers ═══ */
function MetricCell({ value, label, tooltip, color }: { value: string; label: string; tooltip: string; color: string }) {
  return (
    <div title={tooltip} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, cursor: 'help' }}>
      <span style={{ fontWeight: 700, fontSize: 'var(--t-14)', color, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <span style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)', fontWeight: 500 }}>{label}</span>
    </div>
  );
}

/* ═══ ConseillerCard — list card ═══ */
function ConseillerCard({ c, onSelect }: { c: Conseiller; onSelect: () => void }) {
  const { t } = useT();
  const fd = useFormatDate();
  const hasEndingSoon = c.ending_soon > 0;
  return (
    <div className="card card-clickable" onClick={onSelect}
      style={{ borderLeft: hasEndingSoon ? '3px solid var(--warn)' : undefined, padding: 'var(--s12) var(--s16)', marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s8)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 'var(--t-14)' }}>{c.last_name} {c.first_name}</span>
        <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-2)', fontWeight: 500 }}>{c.organization_name || '—'}</span>
        {c.contact_role && c.contact_role !== 'contact' && (
          <span className="badge badge-sm" style={{ fontSize: 'var(--t-10)' }}>{c.contact_role}</span>
        )}
        <span className="stat-pill">{t('conseillers.card.activeCount', { n: c.active_measures })}</span>
        {c.programmes.map(p => <span key={p} className="badge badge-sm">{p}</span>)}
      </div>
      {hasEndingSoon && (
        <div style={{ fontSize: 'var(--t-11)', fontWeight: 500, marginBottom: 'var(--s8)', color: 'var(--warn)' }}>
          {t('conseillers.card.endingSoon', { n: c.ending_soon })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 'var(--s12)', flexWrap: 'wrap' }}>
        <MetricCell value={c.current_ca > 0 ? `${fmtCHF(c.current_ca)}` : '—'} label={t('conseillers.metric.monthlyRevenue')} tooltip={t('conseillers.metric.monthlyRevenueTooltip')} color="var(--accent)" />
        <MetricCell value={c.lifetime_value > 0 ? `${fmtCHF(c.lifetime_value)}` : '—'} label={t('conseillers.metric.lifetimeValue')} tooltip={t('conseillers.metric.lifetimeValueTooltip')} color="var(--ink-1)" />
        <MetricCell value={`${c.total_measures}`} label={t('conseillers.metric.totalMeasures')} tooltip={t('conseillers.metric.completedTooltip', { n: c.completed_measures })} color="var(--ink-1)" />
        <MetricCell value={c.avg_presence_rate !== null ? `${c.avg_presence_rate}%` : '—'} label={t('conseillers.metric.avgPresence')} tooltip={t('conseillers.metric.avgPresenceTooltip')} color={levelColor(presenceLevel(c.avg_presence_rate))} />
        {c.first_referral_date && (
          <MetricCell value={relativeAge(c.first_referral_date, t)} label={t('conseillers.metric.relationship')} tooltip={t('conseillers.metric.firstMandateTooltip', { date: fmtDate(c.first_referral_date, fd) })} color="var(--ink-3)" />
        )}
      </div>
    </div>
  );
}

/* ═══ ParticipantCard — overview card with KPIs + trend ═══ */
function ParticipantCard({ p }: { p: ConseillerParticipant }) {
  const { t } = useT();
  const delta = (p.last_week_hours != null && p.hours_per_week > 0)
    ? p.last_week_hours - p.hours_per_week
    : null;

  return (
    <div className="card card-clickable" style={{ padding: 'var(--s12) var(--s16)', marginBottom: 0 }}
      onClick={() => { window.location.hash = `participant?id=${p.participant_id}`; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s8)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 'var(--t-14)' }}>{p.last_name} {p.first_name}</span>
        <span className="badge badge-sm">{p.programme_code}</span>
        {p.subtype_name && (
          <span style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)' }}>
            {p.subtype_name.length > 35 ? p.subtype_name.slice(0, 35) + '...' : p.subtype_name}
          </span>
        )}
        {p.rm_name && <span style={{ marginLeft: 'auto', fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{t('conseillers.card.rmPrefix', { rm: p.rm_name })}</span>}
      </div>

      <div className="kpi-row" style={{ border: 'none', borderRadius: 0, background: 'transparent' }}>
        {/* Activité + trend */}
        <div className="kpi-card" style={{ borderRight: '1px solid var(--line)' }}>
          <div className="kpi-card__label">
            {t('conseillers.kpi.activity')}
            <InfoTip text={t('conseillers.kpi.activityTooltip')} />
          </div>
          <div className="kpi-card__body">
            <div>
              <span className="kpi-card__value">{p.hours_per_week != null ? p.hours_per_week.toFixed(1).replace('.', ',') : '—'}</span>
              <span className="kpi-card__suffix">{t('conseillers.unit.hoursPerWeek')}</span>
            </div>
            {delta != null && (
              <span className={`kpi-card__delta kpi-card__delta--${delta >= 0 ? 'up' : 'down'}`}
                style={{ marginLeft: 'var(--s8)', whiteSpace: 'nowrap' }}>
                {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1).replace('.', ',')} h
              </span>
            )}
          </div>
        </div>

        {/* Taux d'activité */}
        <div className="kpi-card" style={{ borderRight: '1px solid var(--line)' }}>
          <div className="kpi-card__label">
            {t('conseillers.kpi.activityRate')}
            <InfoTip text={t('conseillers.kpi.activityRateTooltip')} />
          </div>
          <div className="kpi-card__body">
            <div>
              <span className="kpi-card__value kpi-card__value--lg">{p.taux_activite != null ? p.taux_activite : '—'}</span>
              <span className="kpi-card__suffix">%</span>
            </div>
            <ProgressRing value={p.taux_activite ?? 0} max={100} size={36} />
          </div>
        </div>

        {/* Présence */}
        <div className="kpi-card" style={{ borderRight: '1px solid var(--line)' }}>
          <div className="kpi-card__label">
            {t('conseillers.kpi.presence')}
            <InfoTip text={t('conseillers.kpi.presenceTooltip')} />
          </div>
          <div className="kpi-card__body">
            <div>
              <span className="kpi-card__value kpi-card__value--lg" style={{ color: levelColor(presenceLevel(p.presence_rate)) }}>
                {p.presence_rate !== null ? Math.round(p.presence_rate) : '—'}
              </span>
              <span className="kpi-card__suffix">%</span>
            </div>
            <ProgressRing value={p.presence_rate ?? 0} max={100} size={36} />
          </div>
        </div>

        {/* Jours restants + Prix */}
        <div className="kpi-card">
          <div className="kpi-card__label">{t('conseillers.kpi.measure')}</div>
          <div style={{ display: 'flex', gap: 'var(--s12)', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 700, fontSize: 'var(--t-14)', color: levelColor(daysLevel(p.days_remaining)), fontVariantNumeric: 'tabular-nums' }}>
                {p.days_remaining !== null ? `${p.days_remaining} j` : '—'}
              </span>
              <span style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)' }}>{t('conseillers.kpi.remaining')}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 700, fontSize: 'var(--t-14)', color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>
                {p.monthly_price ? `${p.monthly_price.toLocaleString('fr-CH')}` : '—'}
              </span>
              <span style={{ fontSize: 'var(--t-10)', color: 'var(--ink-3)' }}>{t('conseillers.unit.chfPerMonth')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══ ConseillerDetail — full detail view ═══ */
type DetailMetric = 'ca' | 'cumul' | 'mesures' | 'presence';

function ConseillerDetail({ c, onBack }: { c: Conseiller; onBack: () => void }) {
  const { t } = useT();
  const fd = useFormatDate();
  const [expandedMetric, setExpandedMetric] = useState<DetailMetric | null>(null);
  const [participants, setParticipants] = useState<ConseillerParticipant[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueMonth[]>([]);
  const [presenceHistory, setPresenceHistory] = useState<PresenceMonth[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(true);

  const toggleMetric = (key: DetailMetric) => setExpandedMetric(expandedMetric === key ? null : key);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      try {
        const result = await api.getConseillerDetail(c.contact_id) as ConseillerDetailResponse;
        if (!cancelled) {
          setParticipants(result.participants || []);
          setRevenueData(result.revenue || []);
          setPresenceHistory(result.presence_history || []);
        }
      } catch (err) {
        console.error('Failed to load detail:', err);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [c.contact_id]);

  // Chart data derived from revenue
  const caChartData = useMemo(() =>
    revenueData.map(d => ({ month: d.month, value: d.revenue, label: t('conseillers.chart.activeMeasuresLabel', { n: d.active_measures }) })),
  [revenueData, t]);

  const cumulChartData = useMemo(() => {
    let sum = 0;
    return revenueData.map(d => { sum += d.revenue; return { month: d.month, value: sum }; });
  }, [revenueData]);

  const mesuresChartData = useMemo(() =>
    revenueData.map(d => ({ month: d.month, value: d.active_measures })),
  [revenueData]);

  const presenceChartData = useMemo(() =>
    presenceHistory.map(d => ({ month: d.month, value: d.presence_rate })),
  [presenceHistory]);

  // True cumulative total from revenue data (matches the chart's last bar)
  const cumulTotal = useMemo(() => revenueData.reduce((s, d) => s + d.revenue, 0), [revenueData]);

  // Sparkline data
  const caSparkData = useMemo(() => revenueData.slice(-12).map(d => d.revenue), [revenueData]);
  const cumulSparkData = useMemo(() => { let s = 0; return revenueData.map(d => { s += d.revenue; return s; }); }, [revenueData]);
  const mesuresSparkData = useMemo(() => revenueData.slice(-12).map(d => d.active_measures), [revenueData]);
  const presenceSparkData = useMemo(() => presenceHistory.slice(-12).map(d => d.presence_rate), [presenceHistory]);

  const chartTitles: Record<DetailMetric, string> = {
    ca: t('conseillers.chart.title.ca'),
    cumul: t('conseillers.chart.title.cumul'),
    mesures: t('conseillers.chart.title.mesures'),
    presence: t('conseillers.chart.title.presence'),
  };

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 'var(--s12)' }}>
        {t('conseillers.backToAll')}
      </button>

      {/* Header card */}
      <div style={{
        border: '1px solid var(--line)', borderRadius: 'var(--r1)',
        backgroundColor: 'var(--surface)', padding: 'var(--s16)', marginBottom: 'var(--s16)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s16)', flexWrap: 'wrap', marginBottom: 'var(--s16)' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={{ fontSize: 'var(--t-20)', fontWeight: 700, margin: 0, letterSpacing: '-0.015em' }}>
              {c.last_name} {c.first_name}
            </h2>
            <div style={{ fontSize: 'var(--t-13)', color: 'var(--ink-2)', marginTop: 'var(--s4)' }}>
              {c.organization_name || t('conseillers.unknownOrganization')}
              {c.contact_role && c.contact_role !== 'contact' && <span style={{ color: 'var(--ink-3)' }}> — {c.contact_role}</span>}
            </div>
            <div style={{ display: 'flex', gap: 'var(--s8)', marginTop: 'var(--s4)', flexWrap: 'wrap' }}>
              {c.email && <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{c.email}</span>}
              {c.phone && <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{c.phone}</span>}
              {c.first_referral_date && (
                <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
                  {t('conseillers.since', { age: relativeAge(c.first_referral_date, t), date: fmtDate(c.first_referral_date, fd) })}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--s4)', flexWrap: 'wrap' }}>
            {c.programmes.map(p => <span key={p} className="badge badge-sm">{p}</span>)}
          </div>
        </div>

        {/* KPI row — all clickable */}
        <div className="kpi-row">
          {/* CA mensuel */}
          <div className={`kpi-card${expandedMetric === 'ca' ? ' kpi-card--expanded' : ''}`}
            onClick={() => toggleMetric('ca')} style={{ cursor: 'pointer' }}>
            <div className="kpi-card__label">
              {t('conseillers.metric.monthlyRevenue')}
              <InfoTip text={t('conseillers.detail.caTooltip')} />
            </div>
            <div className="kpi-card__body">
              <div>
                <span className="kpi-card__value" style={{ color: 'var(--accent)' }}>{c.current_ca > 0 ? fmtCHF(c.current_ca) : '—'}</span>
                <span className="kpi-card__suffix">{t('conseillers.unit.chf')}</span>
              </div>
            </div>
            {caSparkData.length > 3 && expandedMetric !== 'ca' && (
              <Sparkline data={caSparkData} width={200} height={28} />
            )}
          </div>

          {/* Valeur cumulée */}
          <div className={`kpi-card${expandedMetric === 'cumul' ? ' kpi-card--expanded' : ''}`}
            onClick={() => toggleMetric('cumul')} style={{ cursor: 'pointer' }}>
            <div className="kpi-card__label">
              {t('conseillers.metric.lifetimeValue')}
              <InfoTip text={t('conseillers.detail.cumulTooltip')} />
            </div>
            <div className="kpi-card__body">
              <span className="kpi-card__value">{(cumulTotal || c.lifetime_value) > 0 ? fmtCHF(cumulTotal || c.lifetime_value) : '—'}</span>
              <span className="kpi-card__suffix">{t('conseillers.unit.chf')}</span>
            </div>
            {cumulSparkData.length > 3 && expandedMetric !== 'cumul' && (
              <Sparkline data={cumulSparkData} width={200} height={28} />
            )}
          </div>

          {/* Mesures */}
          <div className={`kpi-card${expandedMetric === 'mesures' ? ' kpi-card--expanded' : ''}`}
            onClick={() => toggleMetric('mesures')} style={{ cursor: 'pointer' }}>
            <div className="kpi-card__label">
              {t('conseillers.detail.measuresLabel')}
              <InfoTip text={t('conseillers.detail.measuresTooltip')} />
            </div>
            <div className="kpi-card__body">
              <div>
                <span className="kpi-card__value">{c.active_measures}</span>
                <span className="kpi-card__suffix">{t('conseillers.unit.active')}</span>
                <div className="kpi-card__sub">{t('conseillers.detail.measuresSub', { total: c.total_measures, completed: c.completed_measures })}</div>
              </div>
            </div>
            {c.ending_soon > 0 && (
              <div className="kpi-card__delta kpi-card__delta--down">
                {t('conseillers.detail.endingSoonDelta', { n: c.ending_soon })}
              </div>
            )}
            {mesuresSparkData.length > 3 && expandedMetric !== 'mesures' && (
              <Sparkline data={mesuresSparkData} width={200} height={28} />
            )}
          </div>

          {/* Présence */}
          <div className={`kpi-card${expandedMetric === 'presence' ? ' kpi-card--expanded' : ''}`}
            onClick={() => toggleMetric('presence')} style={{ cursor: 'pointer' }}>
            <div className="kpi-card__label">
              {t('conseillers.metric.avgPresence')}
              <InfoTip text={t('conseillers.detail.presenceTooltip')} />
            </div>
            <div className="kpi-card__body">
              <div>
                <span className="kpi-card__value kpi-card__value--lg" style={{ color: levelColor(presenceLevel(c.avg_presence_rate)) }}>
                  {c.avg_presence_rate !== null ? `${c.avg_presence_rate}` : '—'}
                </span>
                <span className="kpi-card__suffix">%</span>
              </div>
              <ProgressRing value={c.avg_presence_rate ?? 0} max={100} size={48} />
            </div>
            {presenceSparkData.length > 3 && expandedMetric !== 'presence' && (
              <Sparkline data={presenceSparkData} width={200} height={28} />
            )}
          </div>
        </div>

        {/* Expanded chart panel */}
        {expandedMetric && (
          <div className="metric-detail-panel" style={{ marginTop: 'var(--s12)' }}>
            <div className="metric-detail-panel__title">{chartTitles[expandedMetric]}</div>
            {loadingDetail ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s16)' }}><div className="spinner" /></div>
            ) : (
              <>
                {expandedMetric === 'ca' && (
                  <MonthlyBarChart data={caChartData} unit="CHF" barColor="var(--accent)" />
                )}
                {expandedMetric === 'cumul' && (
                  <MonthlyBarChart data={cumulChartData} unit="CHF" barColor="var(--accent)"
                    formatValue={v => fmtCHF(v)} />
                )}
                {expandedMetric === 'mesures' && (
                  <MonthlyBarChart data={mesuresChartData} unit="" barColor="var(--ink-2)" />
                )}
                {expandedMetric === 'presence' && (
                  <MonthlyBarChart data={presenceChartData} unit="%" barColor="var(--ok)" />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Participants */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)' }}>
        <h3 style={{ fontSize: 'var(--t-15)', fontWeight: 600, margin: 0 }}>{t('conseillers.detail.activeParticipants')}</h3>
        {!loadingDetail && <span className="stat-pill">{participants.length}</span>}
      </div>

      {loadingDetail ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s16)' }}><div className="spinner" /></div>
      ) : participants.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 'var(--t-11)' }}>{t('conseillers.detail.noActiveParticipants')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
          {participants.map(p => <ParticipantCard key={p.participant_id} p={p} />)}
        </div>
      )}
    </div>
  );
}

/* ═══ ConseillersPage — main ═══ */
export function ConseillersPage() {
  const { t } = useT();
  const [conseillers, setConseillers] = useState<Conseiller[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.split('?')[1] || '');
    const id = params.get('id');
    return id ? Number(id) : null;
  });
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('ca');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getConseillers() as ConseillersResponse;
      setConseillers(result.conseillers || []);
    } catch (err) {
      console.error('Failed to load conseillers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (!hash.startsWith('#conseillers')) return;
      const params = new URLSearchParams(hash.split('?')[1] || '');
      const id = params.get('id');
      setSelectedId(id ? Number(id) : null);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const organizations = useMemo(() => {
    const orgs = new Set<string>();
    conseillers.forEach(c => { if (c.organization_name) orgs.add(c.organization_name); });
    return Array.from(orgs).sort();
  }, [conseillers]);

  const filtered = useMemo(() => {
    let list = conseillers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => c.last_name.toLowerCase().includes(q) || c.first_name.toLowerCase().includes(q) || (c.organization_name || '').toLowerCase().includes(q));
    }
    if (orgFilter) list = list.filter(c => c.organization_name === orgFilter);
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'name': return a.last_name.localeCompare(b.last_name);
        case 'organization': return (a.organization_name || '').localeCompare(b.organization_name || '');
        case 'active': return b.active_measures - a.active_measures;
        case 'ca': return b.current_ca - a.current_ca;
        case 'lifetime': return b.lifetime_value - a.lifetime_value;
        case 'total': return b.total_measures - a.total_measures;
        default: return 0;
      }
    });
    return sorted;
  }, [conseillers, search, orgFilter, sortKey]);

  const selected = selectedId !== null ? conseillers.find(c => c.contact_id === selectedId) : null;
  const totalCA = conseillers.reduce((sum, c) => sum + c.current_ca, 0);
  const totalLifetime = conseillers.reduce((sum, c) => sum + c.lifetime_value, 0);
  const totalActive = conseillers.reduce((sum, c) => sum + c.active_measures, 0);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s32)' }}><div className="spinner" /></div>;

  if (selected) {
    return <ConseillerDetail c={selected} onBack={() => { setSelectedId(null); window.location.hash = 'conseillers'; }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', marginBottom: 'var(--s12)', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'var(--t-17)', fontWeight: 700 }}>{t('conseillers.title')}</h1>
        <span className="stat-pill">{t('conseillers.count', { n: conseillers.length })}</span>
        <span className="stat-pill">{t('conseillers.activeMeasuresCount', { n: totalActive })}</span>
        <span className="stat-pill" style={{ color: 'var(--accent)' }}>{t('conseillers.totalCaPill', { amount: fmtCHF(totalCA) })}</span>
        <span className="stat-pill">{t('conseillers.totalLifetimePill', { amount: fmtCHF(totalLifetime) })}</span>
      </div>

      <p style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginBottom: 'var(--s12)' }}>
        {t('conseillers.description')}
      </p>

      <div style={{ display: 'flex', gap: 'var(--s8)', marginBottom: 'var(--s12)', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-wrapper" style={{ maxWidth: 240 }}>
          <input className="search-input" type="text" placeholder={t('conseillers.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="search-clear" onClick={() => setSearch('')}>x</button>}
        </div>
        <select className="form-select" value={orgFilter} onChange={e => setOrgFilter(e.target.value)} style={{ maxWidth: 240 }}>
          <option value="">{t('conseillers.filter.allOrganizations')}</option>
          {organizations.map(org => <option key={org} value={org}>{org}</option>)}
        </select>
        <select className="form-select" value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)} style={{ maxWidth: 200 }}>
          <option value="ca">{t('conseillers.sort.ca')}</option>
          <option value="lifetime">{t('conseillers.sort.lifetime')}</option>
          <option value="active">{t('conseillers.sort.active')}</option>
          <option value="total">{t('conseillers.sort.total')}</option>
          <option value="name">{t('conseillers.sort.name')}</option>
          <option value="organization">{t('conseillers.sort.organization')}</option>
        </select>
        {(search || orgFilter) && filtered.length !== conseillers.length && (
          <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>{filtered.length} / {conseillers.length}</span>
        )}
      </div>

      {filtered.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', textAlign: 'center', padding: 'var(--s32)' }}>{t('conseillers.noneFound')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
          {filtered.map(c => (
            <ConseillerCard key={c.contact_id} c={c}
              onSelect={() => { setSelectedId(c.contact_id); window.location.hash = `conseillers?id=${c.contact_id}`; }} />
          ))}
        </div>
      )}
    </div>
  );
}
